import React, { useEffect, useMemo, useRef, useState } from 'react'

import { NotificationSettings } from '@repo/types'

import { useSnackbar } from 'components/Snackbar'
import { Divider, PageContainer, PageHeader, PreferenceChannelHeader, PreferenceRow, SectionLabel } from 'components/UI'
import { PROSE_MAX_WIDTH } from 'constants/layout'
import { NotificationsSkeleton } from './NotificationsSkeleton'
import { ChannelAddressRow } from './ChannelAddressRow'
import { PhoneVerificationDrawer } from 'components/UI/PhoneVerificationDrawer'
import { EditEmailDrawer } from 'components/UI/EditEmailDrawer'
import { useAuthFactorsQuery } from 'hooks/api/useAuthFactors'

import { useDispatch, useSelector } from 'store/hooks'
import { updateProfile } from 'store/auth/actions'
import { IValidationCallbackProps } from 'store/model'

import { Stack, Box, useTheme } from '@mui/material'
import { ROUTES } from 'routes'
import { usePushPage } from 'hooks/usePushPage'

/**
 * VERA-291 — certificate-owner notification preferences.
 *
 * The backend gates sends on exactly two owner-mutable categories (see
 * notification-category.classification.ts): `scans` and `transfers`. Each is
 * shown as an Email + SMS switch pair (slice 3, batch 4) — no in-app/platform
 * column since there's no in-app notification center for owners yet, so the
 * Email switch still writes `platform` in step with `email`. SMS is a
 * separate, independently-persisted, opt-in toggle gated on phone
 * verification (see ChannelAddressRow / PhoneVerificationDrawer).
 *
 * VRG-490 batch 8 adds `account` and `security` — the two categories every
 * template in this page's own header copy already promises are "always
 * sent" by email. Both stay opt-in for SMS, same as scans/transfers, with
 * ONE difference: once a user is enrolled in SMS-based 2FA, `security`'s SMS
 * switch is forced on and can't be turned off — enrolling IS the consent
 * event (see two-factor-auth-design.md decision D4). Gated specifically on
 * SMS enrollment, not 2FA in general — a TOTP-only user has no verified
 * phone on file for this, so nothing is forced for them.
 */
const CATEGORIES: Record<string, { title: string; description: string }> = {
  scans: {
    title: 'Scan alerts',
    description: 'When someone scans one of your certificates.',
  },
  transfers: {
    title: 'Transfer updates',
    description: 'When a transfer involving you is started, accepted, or cancelled.',
  },
  account: {
    title: 'Account updates',
    description: 'Welcome messages, invitations, gifts, and ownership confirmations.',
  },
  security: {
    title: 'Security alerts',
    description: 'Password changes and transfer confirmation codes.',
  },
}

const SMS_LOCKED_REASON = 'Verify your phone number to turn on SMS notifications.'

// VRG-490 — `account` and `security` are always sent by email (see this
// page's own header copy above). The Email switch on those two rows is
// rendered checked and disabled rather than omitted, so the grid still reads
// the same shape as every other row (working-conventions §46: a disabled
// control needs a reason the user can read).
const CATEGORY_EMAIL_LOCKED_REASON = "Account and security emails are always sent. This can't be turned off."

// VRG-490 — shown on `security`'s SMS switch once the user is enrolled in
// SMS-based two-factor authentication. Enrolling is the consent event (see
// two-factor-auth-design.md decision D4), so the switch is forced on rather
// than merely defaulted on.
const SECURITY_SMS_FORCED_REASON =
  "Turned on when you enrolled in text message verification. It stays on while text verification is active."

const buildDefaultPrefs = (): NotificationSettings =>
  Object.keys(CATEGORIES).reduce((acc, key) => {
    acc[key] = { platform: true, email: true }
    return acc
  }, {} as NotificationSettings)

export const NotificationsPage = () => {
  const theme = useTheme()
  const { pageSx, goBack } = usePushPage(ROUTES.PROFILE)
  const dispatch = useDispatch()
  const { openSnackbar } = useSnackbar()

  const { profile } = useSelector(({ auth }) => auth)

  // `profile` is a fresh object every SET_PROFILE dispatch, so depending on it
  // directly would recompute initialPrefs (and re-setState) every render →
  // an infinite update loop. Depend on the serialized notification_settings
  // instead — a stable primitive that only changes when the saved content does.
  const savedKey = JSON.stringify(profile?.notification_settings ?? null)

  const initialPrefs = useMemo<NotificationSettings>(() => {
    const saved = profile?.notification_settings
    if (saved && Object.keys(saved).length > 0) {
      return { ...buildDefaultPrefs(), ...saved }
    }
    return buildDefaultPrefs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey])

  const [prefs, setPrefs] = useState<NotificationSettings>(initialPrefs)
  // Mirror the latest prefs in a ref so rapid successive toggles each compute
  // their payload from the freshest state, not a stale render closure.
  const prefsRef = useRef(prefs)

  useEffect(() => {
    setPrefs(initialPrefs)
    prefsRef.current = initialPrefs
  }, [initialPrefs])

  const [emailConsent, setEmailConsent] = useState<boolean>(false)
  const [smsConsent, setSmsConsent] = useState<boolean>(false)
  const [phoneDrawerOpen, setPhoneDrawerOpen] = useState<boolean>(false)
  const [emailDrawerOpen, setEmailDrawerOpen] = useState<boolean>(false)

  useEffect(() => {
    setEmailConsent(Boolean(profile?.email_marketing_consent))
    setSmsConsent(Boolean(profile?.sms_marketing_consent))
  }, [profile?.email_marketing_consent, profile?.sms_marketing_consent])

  const phoneVerified = Boolean(profile?.phone_verified)

  // VRG-490 — is this owner enrolled in SMS-based 2FA? Gated on SMS
  // specifically (not "has any 2FA factor") — a TOTP-only user has no
  // verified phone on file for this, so nothing should be forced for them.
  const { factors: authFactors } = useAuthFactorsQuery()
  const smsTwoFactorEnrolled = authFactors.some(factor => factor.type === 'sms' && factor.verified)

  // `channel` is 'email' or 'sms', written independently. Email still keeps
  // `platform` in step with itself (no in-app center yet, see header comment).
  // SMS is opt-in and never defaulted — an absent `sms` key means off.
  const handleToggle = (key: string, channel: 'email' | 'sms', checked: boolean) => {
    const previous = prefsRef.current
    const nextEntry =
      channel === 'email'
        ? { ...previous[key], platform: checked, email: checked }
        : { ...previous[key], sms: checked }
    const next: NotificationSettings = { ...previous, [key]: nextEntry }

    // Optimistic, per-switch update — no global disable, so the row flips
    // instantly with no flash of the other toggles while the save is in flight.
    prefsRef.current = next
    setPrefs(next)

    dispatch(
      updateProfile({ notification_settings: next } as any, ({ success }: IValidationCallbackProps) => {
        if (!success) {
          prefsRef.current = previous
          setPrefs(previous) // revert on failure
          openSnackbar({ severity: 'error', title: 'Could not update notification setting' })
        }
      })
    )
  }

  const persistMarketing = (patch: { email_marketing_consent?: boolean; sms_marketing_consent?: boolean }) => {
    dispatch(updateProfile(patch as any, () => {}))
  }

  if (!profile) {
    return <NotificationsSkeleton />
  }

  return (
    <Stack
      sx={{
        // Pushed from Profile's Notifications row, so it enters from the RIGHT
        // and leaves back out to the right (usePushPage). Mobile only.
        ...pageSx,
        background: theme.palette.background.paper,
        // Fill the viewport so the page background doesn't stop at the end of
        // the content and expose the dark backdrop beneath it. Matches every
        // other full page in this app (CertificatePage, Signup, NotFound, …).
        minHeight: '100vh',
        // NO `px` — the side gutter belongs to `PageContainer` below, which owns
        // it for every page.
        // NO `pt` — PageHeader owns the top gap (see its note). This page setting
        // its own is what made the gap above the back-link 32px and the gap below
        // it 12px.
        pb: { xs: 4, md: 8 },
      }}
    >
      {/* Shared column outside, READING measure inside — a visual no-op that moves
          the gutter and the outer cap to one owner. See Profile for the full note
          and `PROSE_MAX_WIDTH` for why 640 stays. */}
      <PageContainer>
      <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
        {/*
          VERA-315 — titled "Notifications", and a PUSHED SUB-PAGE under Profile
          rather than a bottom-bar destination.

          It was previously the Activity tab, named "Activity" so the tab and the
          page it opened would at least agree. But the honest reading is that
          this is a settings surface: contact addresses and delivery-channel
          switches. It belongs with the other account settings, and the Activity
          tab now holds the cross-certificate feed its name always implied (the
          missing piece was an API endpoint, which this ticket added).

          The back-link RETURNS: unlike a bottom-bar destination, this genuinely
          is somewhere you came FROM — the Notifications row on Profile — so the
          trail is real. The description stays here, attached to the switches it
          qualifies, per working-conventions §40.
        */}
        <PageHeader
          backLabel='Profile'
          onBack={goBack}
          title='Notifications'
          description="Security and sign-in emails, like passcodes and ownership receipts, are always sent. They can't be turned off."
        />

        {/*
          Both delivery addresses sit here, above the grid whose Email/SMS
          columns they feed — "which address does this actually go to?" belongs
          next to the switches, not only on Profile.

          Both rows open a shared, self-contained drawer (EditEmailDrawer /
          PhoneVerificationDrawer under components/UI) rather than each page
          hand-rolling its own copy — see working-conventions §36. Profile uses
          the same two drawers for its own Email/Phone rows.
        */}
        <SectionLabel first>Contact</SectionLabel>

        <Stack mb={3}>
          <ChannelAddressRow
            label='Email address'
            value={profile?.email}
            emptyHint='No email address on file.'
            actionLabel='Change'
            onAction={() => setEmailDrawerOpen(true)}
          />
          <ChannelAddressRow
            label='Phone number'
            value={profile?.phone}
            verified={phoneVerified}
            emptyHint='Add a phone number to enable SMS notifications.'
            unverifiedHint='Not verified yet. SMS notifications are off until this number is verified.'
            actionLabel={phoneVerified ? 'Change' : profile?.phone ? 'Verify' : 'Add phone'}
            onAction={() => setPhoneDrawerOpen(true)}
          />
        </Stack>

        <Divider />

        {/*
          One grid, one set of column captions. Previously each category was its
          own section with its own "Email"/"SMS" pair, which repeated the same
          two words four times and left only 8px between a caption and the
          switch under it. The section headings ("Scans" / "Transfers") were
          also saying much the same as the row titles beneath them, so with one
          row per category they earned their place back as pure repetition.

          The two `SectionLabel`s here are NOT a reversal of that. They label the
          page's two GROUPS — the contact addresses vs the per-category switches
          — each of which holds 2+ rows. Per-CATEGORY headings stay removed: with
          one row per category they would be single-child groups (§20) and would
          restate the row title directly beneath them. Add a per-category heading
          only when a category grows a second row (e.g. Scans splitting into "my
          certificates" vs "certificates I transferred out"), which is also the
          point the 298-style breakdown starts earning its chrome.
        */}
        <PreferenceChannelHeader
          labels={['Email', 'SMS']}
          sectionLabel={<SectionLabel inline>Notifications</SectionLabel>}
        />

        <PreferenceRow
          title={CATEGORIES.scans.title}
          description={CATEGORIES.scans.description}
          switches={[
            {
              checked: prefs.scans?.email ?? true,
              onChange: checked => handleToggle('scans', 'email', checked),
            },
            {
              checked: prefs.scans?.sms ?? false,
              onChange: checked => handleToggle('scans', 'sms', checked),
              disabled: !phoneVerified,
              disabledReason: phoneVerified ? undefined : SMS_LOCKED_REASON,
            },
          ]}
        />

        <PreferenceRow
          title={CATEGORIES.transfers.title}
          description={CATEGORIES.transfers.description}
          switches={[
            {
              checked: prefs.transfers?.email ?? true,
              onChange: checked => handleToggle('transfers', 'email', checked),
            },
            {
              checked: prefs.transfers?.sms ?? false,
              onChange: checked => handleToggle('transfers', 'sms', checked),
              disabled: !phoneVerified,
              disabledReason: phoneVerified ? undefined : SMS_LOCKED_REASON,
            },
          ]}
        />

        {/*
          VRG-490 — `account` and `security` are always sent by email (this
          page's own header copy already says so), so the Email switch on
          both rows is a dead control: checked, disabled, tooltip explaining
          why (working-conventions §46). `account`'s SMS is ordinary opt-in,
          gated on phone verification like scans/transfers above.
        */}
        <PreferenceRow
          title={CATEGORIES.account.title}
          description={CATEGORIES.account.description}
          switches={[
            {
              checked: true,
              disabled: true,
              disabledReason: CATEGORY_EMAIL_LOCKED_REASON,
              onChange: () => {},
            },
            {
              checked: prefs.account?.sms ?? false,
              onChange: checked => handleToggle('account', 'sms', checked),
              disabled: !phoneVerified,
              disabledReason: phoneVerified ? undefined : SMS_LOCKED_REASON,
            },
          ]}
        />

        {/*
          `security`'s SMS switch has a third state beyond
          on/off-gated-by-phone-verification: once the owner is enrolled in
          SMS-based 2FA, it's forced on — enrolling in text verification IS
          the consent event for these two templates (transfer confirmation
          codes, password-changed alerts). See two-factor-auth-design.md
          decision D4. Gated on SMS enrollment specifically, not 2FA in
          general (`smsTwoFactorEnrolled` above).
        */}
        <PreferenceRow
          title={CATEGORIES.security.title}
          description={CATEGORIES.security.description}
          switches={[
            {
              checked: true,
              disabled: true,
              disabledReason: CATEGORY_EMAIL_LOCKED_REASON,
              onChange: () => {},
            },
            smsTwoFactorEnrolled
              ? {
                  checked: true,
                  disabled: true,
                  disabledReason: SECURITY_SMS_FORCED_REASON,
                  onChange: () => {},
                }
              : {
                  checked: prefs.security?.sms ?? false,
                  onChange: checked => handleToggle('security', 'sms', checked),
                  disabled: !phoneVerified,
                  disabledReason: phoneVerified ? undefined : SMS_LOCKED_REASON,
                },
          ]}
        />

        {/*
          Marketing sits in the SAME grid rather than as two single-switch rows.
          It is the same question the rows above ask ("which channels may we use
          for this?"), so giving it a different shape on the same page read as
          an inconsistency. Note the values still write the marketing-consent
          columns, NOT notification_settings — a legally distinct thing that
          happens to share the layout.
        */}
        <PreferenceRow
          title='Marketing'
          description='Product news, offers, and updates.'
          switches={[
            {
              checked: emailConsent,
              onChange: checked => {
                setEmailConsent(checked)
                persistMarketing({ email_marketing_consent: checked })
              },
            },
            {
              checked: smsConsent,
              onChange: checked => {
                setSmsConsent(checked)
                persistMarketing({ sms_marketing_consent: checked })
              },
            },
          ]}
        />

      </Box>
      </PageContainer>

      <PhoneVerificationDrawer
        open={phoneDrawerOpen}
        onClose={() => setPhoneDrawerOpen(false)}
        initialPhone={profile?.phone}
        surface='notifications'
      />

      <EditEmailDrawer
        open={emailDrawerOpen}
        initialEmail={profile?.email}
        onClose={() => setEmailDrawerOpen(false)}
      />
    </Stack>
  )
}

export default NotificationsPage
